import { BaseSandbox } from '@app/core/store/base.sandbox';
import { Injectable, inject } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { CoreState } from '@app/core/store/core.slice';
import { Observable, firstValueFrom, map, of, tap } from 'rxjs';
import {
  clientConfigApplicableState,
  employeeSelector,
  getClientConfig,
} from '@app/home/store/home.slice';
import { HomeService } from '@app/core/services/home.service';
import { ClientConfig, EmployeeType } from '@app/core/types/config/config.types';
import { FeatureFlags } from '@app/core/feature-flags/feature-flag.types';
import { App } from '@capacitor/app';
import { FeatureFlagStore } from '@app/core/feature-flags/feature-flag.store';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';
import { ProfileService } from '@app/core/services/profile.service';

@Injectable({ providedIn: 'root' })
export class HomeSandbox extends BaseSandbox {
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly translationSandbox = inject(TranslationSandbox);
  private readonly profileService = inject(ProfileService);

  workFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Work);
  calendarFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Calendar);
  eventCalendarFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.EventCalendar);
  housingFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Housing);
  jobSearchFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Job);
  shiftFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Shifts);

  employee$ = this.store.select(employeeSelector);
  clientConfigLoading$: Observable<boolean> = this.store.select(clientConfigApplicableState.loading);
  isZZP$ = this.employee$.pipe(map((employee) => employee?.type === EmployeeType.Freelancer));

  onResumeListener: PluginListenerHandle;

  private applicationLanguageSync: Promise<void> | null = null;

  constructor(store: Store<CoreState>, private homeService: HomeService) {
    super(store);
  }

  /**
   * The client config is localized server-side on the culture header, so the user's profile
   * language has to be settled before it goes out. Memoized: one fetch per session.
   * A failure is deliberately swallowed — the config then goes out with the language the app
   * already had, which is no worse than before, and the home load is never blocked on this.
   */
  ensureApplicationLanguage(): Promise<void> {
    this.applicationLanguageSync ??= firstValueFrom(this.profileService.getApplicationLanguage())
      .then(({ applicationLanguage }) => {
        if (applicationLanguage?.cultureCode) {
          this.translationSandbox.setApplicationLanguage(applicationLanguage);
          this.translationSandbox.changeLanguage(applicationLanguage.cultureCode);
        }
      })
      .catch(() => undefined);

    return this.applicationLanguageSync;
  }

  getClientConfig(): Observable<ClientConfig> {
    return this.homeService.getClientConfig()
      .pipe(
        tap((clientConfig) => {
          this.load(getClientConfig, {}, of(clientConfig));
          if (clientConfig?.featureFlags) {
            this.featureFlagStore.updateFeatureFlags(clientConfig.featureFlags);
          }

          if (clientConfig?.applicationLanguage) {
            this.translationSandbox.setApplicationLanguage(clientConfig?.applicationLanguage);
            this.translationSandbox.changeLanguage(clientConfig?.applicationLanguage.cultureCode);
          }
        })
      );
  }

  async setOnResumeListener(): Promise<void> {
    this.onResumeListener = await App.addListener('resume', () => this.getClientConfig().subscribe());
  }

  cleanUpListeners(): void {
    if (this.onResumeListener) {
      this.onResumeListener.remove();
      this.onResumeListener = null;
    }

    this.applicationLanguageSync = null;
  }
}
